/** Supabase accounts (R4) — the go-forward sign-in provider for the studio.
 *
 * Mirrors `accounts-firebase.ts`'s contract exactly (see `accounts.ts` for the
 * shared `Accounts` shape and the dispatcher). A Supabase access token is a
 * normal JWT with an `exp` claim, so it slots into the SAME
 * `graphmind_id_token` cookie lifecycle in `@/lib/auth-token` — that module
 * stays entirely provider-agnostic; it never imports this file or the
 * Supabase SDK.
 */

import { api } from "@/lib/api";
import { setTokenProvider, writeIdCookie } from "@/lib/auth-token";
import type { AccountUser, Accounts } from "@/lib/accounts";

interface SupabaseConfigOut {
  enabled: boolean;
  url: string;
  anon_key: string;
}

interface SupabaseAuthUser {
  id: string;
  email?: string | null;
  user_metadata?: Record<string, unknown>;
}

/** Resolve Supabase-backed accounts, or `null` if unconfigured (older
 * backend, or the operator hasn't set SUPABASE_* env vars — Firebase then
 * gets a turn via `accounts.ts`'s dispatcher). */
export async function initSupabaseAccounts(): Promise<Accounts | null> {
  let config: SupabaseConfigOut;
  try {
    config = await api.request<SupabaseConfigOut>("GET", "/api/auth/supabase/config");
  } catch {
    return null; // older backend
  }
  if (!config.enabled) return null;

  const { createClient } = await import("@supabase/supabase-js");
  const supabase = createClient(config.url, config.anon_key, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });

  // Let the token layer pull a freshly-minted access token on demand
  // (proactive timer, visibility/online wake-ups, and 401 recovery all route
  // through here). `refreshSession` mints a new token; if that comes back
  // empty (e.g. offline, or nothing to refresh yet right after a redirect)
  // fall back to whatever session is already cached rather than reporting
  // "signed out" from under the user.
  setTokenProvider(async () => {
    const { data } = await supabase.auth.refreshSession();
    return data.session?.access_token ?? (await supabase.auth.getSession()).data.session?.access_token ?? null;
  });

  // Keep the cookie in lock-step with EVERY Supabase auth event: sign-in,
  // sign-out, the SDK's own silent refresh, AND the OAuth-redirect restore
  // that `detectSessionInUrl` performs when the browser returns from Google.
  supabase.auth.onAuthStateChange((_event, session) => {
    writeIdCookie(session?.access_token ?? null);
  });

  const toUser = (u: SupabaseAuthUser): AccountUser => ({
    uid: u.id,
    email: u.email ?? null,
    displayName: (u.user_metadata?.full_name as string | undefined) ?? null,
    photoURL: (u.user_metadata?.avatar_url as string | undefined) ?? null,
  });

  return {
    provider: "supabase",
    signInWithGoogle: async () => {
      // Redirect flow, unlike Firebase's popup: the browser navigates away, so
      // there's no user to return here. `onAuthStateChange` (above) writes the
      // cookie once the app reloads at `redirectTo` and `detectSessionInUrl`
      // restores the session.
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
    },
    signInWithPassword: async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (!data.session || !data.user) throw new Error("Sign-in did not return a session");
      writeIdCookie(data.session.access_token);
      return toUser(data.user);
    },
    signUp: async (email, password) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        // The verification link must land back on THIS app so detectSessionInUrl
        // can consume the token and complete sign-in.
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      if (!data.user) throw new Error("Sign-up did not return a user");
      // A session is only present when email confirmation isn't required; if it
      // IS required there's no token yet, so the cookie stays clear until the
      // user clicks the verification link (which restores the session on return).
      if (data.session) writeIdCookie(data.session.access_token);
      return { user: toUser(data.user), needsVerification: !data.session };
    },
    resendVerification: async (email) => {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
    },
    resetPassword: async (email) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
    },
    updatePassword: async (password) => {
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // updateUser returns a refreshed session on the current device.
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (token) writeIdCookie(token);
      void data;
    },
    updateEmail: async (email) => {
      // Supabase emails a confirmation to the NEW address; the change applies
      // only once that link is followed.
      const { error } = await supabase.auth.updateUser(
        { email },
        { emailRedirectTo: window.location.origin },
      );
      if (error) throw error;
    },
    onPasswordRecovery: (cb) => {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event) => {
        if (event === "PASSWORD_RECOVERY") cb();
      });
      return () => subscription.unsubscribe();
    },
    signOut: async () => {
      await supabase.auth.signOut();
      writeIdCookie(null);
    },
    onUser: (cb) => {
      // Fire immediately with whatever session is already cached (mirrors
      // Firebase's onIdTokenChanged, which also calls back right away), then
      // track every subsequent auth event.
      void supabase.auth.getSession().then(({ data }) => {
        cb(data.session?.user ? toUser(data.session.user) : null);
      });
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        cb(session?.user ? toUser(session.user) : null);
      });
      return () => subscription.unsubscribe();
    },
  };
}
